import { createSelector } from 'reselect';
import * as enrollmentsConstants from '@benniehealth/core-js/constants/enrollments';
import perksStore from '@benniehealth/core-js/store/perks';
import profileStore from '@benniehealth/core-js/store/profile';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _camelCase from 'lodash/camelCase';

import { t as defaultT } from '@app/i18n';

export const featureFlagsSelector = (_state, ownProps) => {
  return ownProps.flags;
};

export const getSelectedEnrollmentKey = (state, ownProps) => {
  const enrollmentFromRoute = _get(ownProps, 'match.params.enrollment');
  const hash = _get(ownProps, 'location.hash');
  const enrollmentFromHash = hash ? hash.split('#')[1] : null;
  return _camelCase(enrollmentFromRoute || enrollmentFromHash);
};

export const hasValue = field => !_isNil(field[1]) && !_isEmpty(field[1]);

export const getCardColumns = enrollmentKey =>
  createSelector(
    profileStore.selectors.enrollmentSelector(enrollmentKey),
    (_state, _ownProps, config = {}) => config.language,
    (_state, _ownProps, config = {}) => config.translator ?? defaultT,
    (enrollment, _language, translator) => {
      if (!enrollment) {
        return null;
      }
      const t = translator;
      if (
        enrollmentKey === enrollmentsConstants.ENROLLMENT_TYPE_SLUGS.MEDICAL
      ) {
        return {
          title: enrollment.carrierName,
          subtitle: enrollment.displayPlanName,
          info: [
            [
              [t('insuranceCardFields.group'), enrollment.groupId],
              [t('insuranceCardFields.memberId'), enrollment.memberId],
            ].filter(hasValue),
            [
              [t('insuranceCardFields.rxBin'), enrollment.rxBin],
              [t('insuranceCardFields.rxPcn'), enrollment.rxPcn],
            ].filter(hasValue),
          ],
          costs: [
            [
              t('insuranceCardFields.primaryCopay'),
              enrollment.primaryCopayAmount,
            ],
            [
              t('insuranceCardFields.specialistCopay'),
              enrollment.specialistCopayAmount,
            ],
            [t('insuranceCardFields.retailRx'), enrollment.retailRx],
          ].filter(hasValue),
        };
      }
      if (enrollmentKey === enrollmentsConstants.ENROLLMENT_TYPE_SLUGS.DENTAL) {
        return {
          title: enrollment.carrierName,
          subtitle: enrollment.displayPlanName,
          info: [
            [[t('insuranceCardFields.group'), enrollment.groupId]].filter(
              hasValue,
            ),
            [
              [t('insuranceCardFields.memberId'), enrollment.memberId],
            ].filter(hasValue),
          ],
          costs: [
            [t('insuranceCardFields.deductible'), enrollment.inDeductible],
            [
              t('insuranceCardFields.preventiveServices'),
              enrollment.preventiveServices,
            ],
            [
              t('insuranceCardFields.basicServices'),
              enrollment.basicServices,
            ],
            [
              t('insuranceCardFields.majorServices'),
              enrollment.majorServices,
            ],
            [
              t('insuranceCardFields.calendarYearMaximum'),
              enrollment.calendarYearMaximum,
            ],
          ].filter(hasValue),
        };
      }
      if (enrollmentKey === enrollmentsConstants.ENROLLMENT_TYPE_SLUGS.VISION) {
        return {
          title: enrollment.carrierName,
          subtitle: enrollment.displayPlanName,
          info: [
            [[t('insuranceCardFields.group'), enrollment.groupId]].filter(
              hasValue,
            ),
            [
              [t('insuranceCardFields.memberId'), enrollment.memberId],
            ].filter(hasValue),
          ],
          costs: [
            [
              t('insuranceCardFields.examCopay'),
              `${enrollment.examCopay}`,
            ],
          ].filter(hasValue),
        };
      }
      return null;
    },
  );

export const getIsHomePageDataReady = createSelector(
  perksStore.selectors.getHasFetchedPerks,
  profileStore.selectors.hasLoadedProfileSelector,
  (hasFetchedPerks, hasLoadedProfile) => {
    return hasFetchedPerks && hasLoadedProfile;
  },
);

export const hasEnrollmentsOrPerksSelector = createSelector(
  profileStore.selectors.getEnrollments,
  profileStore.selectors.getHasPendingMdv,
  perksStore.selectors.getPerks,
  (enrollments, hasPendingMdv, perks) => {
    const hasEnrollments = !_isEmpty(enrollments);
    const hasPerks = !_isEmpty(perks);

    return hasEnrollments || hasPendingMdv || hasPerks;
  },
);

export const getHasActiveEnrollments = createSelector(
  profileStore.selectors.getEnrollments,
  enrollments => {
    return !_isEmpty(enrollments);
  },
);

export const planNetworkMapsSelector = createSelector(
  profileStore.selectors.getPlanNetworkMapsWithEnrollmentKey(
    enrollmentsConstants.ENROLLMENT_TYPE_SLUGS.MEDICAL,
  ),
  profileStore.selectors.mdvPlanNetworkMapsSelector,
  featureFlagsSelector,
  (medicalPlanNetworkMaps, mdvPlanNetworkMaps, featureFlags) => {
    if (featureFlags.dentalAndVisionSearch) {
      return mdvPlanNetworkMaps;
    }
    return medicalPlanNetworkMaps;
  },
);

export const planNetworkMapsIdsSelector = createSelector(
  planNetworkMapsSelector,
  planNetworkMaps => {
    return planNetworkMaps.map(({ networkId }) => networkId);
  },
);
